import Vue from 'vue';
import {
    createUUIDv4,
    getWindowDimensions,
    isEqual,
    numberRangeCheck,
} from '../util/helpers';
import { ApckPluginManagerFieldV2 as ApckPluginManagerField } from './scans/scan_plugins_v2.js';
import AjaxUtils from "../util/ajax_utils";
import { ApckDropdown } from './common/dropdown';
import { ApckPropertyTable } from './common/property_table';
import Datepicker from "vuejs-datepicker";
import {eventBus} from "../util/eventbus";
import { get, set } from 'lodash';

export const ApckSection = {
    components: {
        'apck-plugin-manager-field': ApckPluginManagerField,
        'apck-property-table': ApckPropertyTable,
    },
    props: {
        fields: Array,
        title: String,
        url: String,
        collapsed: {
            type: Boolean,
            default: false,
        },
        collapsible: {
            type: Boolean,
            default: true,
        },
        header: {
            type: Boolean,
            default: true,
        },
        errors: {
            type: Object,
            default: null,
        },
        localValue: {
            type: Object,
            default: null,
        },
    },
    data: function () {
        return {
            isCollapsed: this.collapsed,
            showHeader: this.header,
        };
    },
    methods: {
        toggle: function (event) {
            if (this.collapsible) {
                this.isCollapsed = !this.isCollapsed;
            }
        },
        changeOption: function (data) {
            this.$emit('changeOption', data);
        },
        checkPermissions: function (permission) {
            if (!this.$permissions) return false;
            const isUserPermission = this.$permissions[permission] === true;
            const isOrganisationPermission = this.$permissions.organisation?.[permission] === true;
            return isUserPermission || isOrganisationPermission;
        },
        emitInput(mapping, value) {
            this.$emit('field-input', { mapping, value });
        },
        getFieldValue(field, localValue) {
            return get(localValue, field.full_mapping);
        },
        getComponentName(field) {
            const mapField = {
                boolean: 'apck-checkbox',
                upload: 'apck-text-upload',
                text: 'apck-text',
                int: 'apck-text',
                password: 'apck-password',
                goscript: 'apck-goscript-import',
                select: 'apck-select',
                selection: 'apck-select',
                info: 'apck-field-info',
                // button: 'apck-field-button',
                property: 'apck-property-table',
                credential: 'apck-credential-import',
            };
            return mapField[field.type];
        },
        getFieldProps(field) {
            const options = {};
            const baseProps = ['label', 'name', 'placeholder', 'rows'];

            baseProps.forEach((key) => {
                if (field[key] !== undefined) {
                    options[key] = field[key];
                }
            });

            if (field.type === 'int') {
                options.type = 'number';
                if (field.minimum !== undefined) options.min = field.minimum;
                if (field.maximum !== undefined) options.max = field.maximum;
            }
            if (field.type === 'select') options.options = field.options;
            if (field.type === 'selection') options.options = field.selection;
            if (field.type === 'password') options['show-password'] = this.checkPermissions('password_unmasking');
            return options;
        },
        validateInput(event) {
            event.target.reportValidity();
        }
    },
    template: `
        <fieldset
            v-bind:class="[
                !isCollapsed ? 'collapsible-open' : '',
                showHeader ? '' : 'no-header',
                'collapsible',
                errors ? 'collapsible--error' : ''
            ]"
        >
            <div
                v-if="showHeader"
                v-on:click="toggle()"
                class="header"
            >
                <span class="title">
                    {{ title }}
                </span>

                <div class="header-tbar">
                    <slot name="tbar" />
                </div>

                <div v-if="collapsible" class="collapsible-toggle">
                    <i v-if="!isCollapsed" class="fa fa-angle-down"></i>
                    <i v-else class="fa fa-angle-up"></i>
                </div>
            </div>

            <div class="inner" v-show="!isCollapsed || !showHeader">
                <slot></slot>
                <ul class="options">
                    <li class="option" v-for="field in fields">
                        <apck-field
                            v-if="field.type != 'section'"
                            :description="field.description"
                        >
                            <component
                                :is="getComponentName(field)"
                                :value="getFieldValue(field, localValue)"
                                v-on:input="emitInput(field.full_mapping, $event)"
                                v-bind="getFieldProps(field)"
                                @focusout.native="validateInput($event)"
                            />
                        </apck-field>

                        <apck-tabs
                            v-if="field.type == 'tabs'"
                            :title="field.title"
                        >
                            <apck-tab
                                v-for="(tab, index) in field.tabs"
                                :title="tab.title"
                                :active="index == 0"
                                :key="index"
                            >
                                <apck-section
                                    v-if="tab.type == 'section'"
                                    :fields="tab.fields"
                                    :header="false"
                                    v-on:changeOption="changeOption"
                                    :local-value="localValue"
                                    v-on:field-input="$emit('field-input', $event)"
                                >
                                </apck-section>
                            </apck-tab>
                        </apck-tabs>

                        <apck-section
                            v-if="field.type == 'section'"
                            :title="field.title"
                            :fields="field.fields"
                            :collapsed="true"
                            v-on:changeOption="changeOption"
                            :local-value="localValue"
                            v-on:field-input="$emit('field-input', $event)"
                        >
                        </apck-section>

                        <apck-plugin-manager-field
                            v-if="field.type == 'plugin'"
                            :fields="field.fields"
                        ></apck-plugin-manager-field >

                    </li>
                </ul>
                <slot name="bottom"></slot>
            </div>
        </fieldset>
    `,
};


export const ApckFieldInfo = {
    props: {
        icon: {
            type: String,
            default: "fa-info"
        }
    },
    template: `
        <div class="field-info">
            <label class="label">
                <i :class="['fa', icon]"></i>
                <span><slot/></span>
            </label>
        </div>
    `
}

export const ApckFieldButton = {
    props: {
        title: {
            type: String,
            default: ""
        },
        value: {
            type: String,
            default: ""
        },
        icon: {
            type: String,
            default: ""
        },
        href: {
            type: String,
            default: ""
        }
    },
    methods: {
        buttonHref: function () {
            window.open(this.href, '_blank');
            return false;
        }
    },
    template: `
        <div class="col">
            <apck-button
                :title="title"
                :value="value"
                :icon="icon"
                class="btn-primary"
                :handler="buttonHref"
            />
        </div>
    `
}

export const ApckLorem = {
    template: `
        <div class="text-wrap">
            <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                Cras bibendum placerat mi, quis semper quam ultricies in.
                Nam interdum justo urna, id euismod metus tincidunt a.
                Praesent mollis metus quis viverra lacinia. Etiam interdum dui eu lectus dapibus,
                vel suscipit est iaculis. Sed congue auctor enim ac varius.
                Fusce ut sem vitae lacus dapibus fringilla. Cras vulputate turpis vitae eros efficitur,
                in vulputate velit faucibus. Curabitur sodales sodales scelerisque.
                Etiam sodales purus sed eleifend fringilla. Cras sollicitudin nulla sed massa elementum,
                ut molestie nulla mattis. Aliquam erat volutpat.
            </p>
            <p style="display:none">
                In hac habitasse platea dictumst. Morbi euismod finibus eros,
                quis elementum sem vestibulum ornare. Ut nec mollis massa,
                eget tincidunt mi. Vestibulum auctor mauris lacus.
                Cras sodales sollicitudin ligula sit amet luctus.
                Sed nisl neque, mattis quis justo ut, fermentum laoreet ipsum. Fusce tincidunt ornare elit,
                et faucibus ipsum sollicitudin quis. Suspendisse auctor pellentesque metus.
                Aliquam et sodales lacus, vel rutrum turpis. Ut eget dignissim tellus.
                Phasellus vitae egestas tellus. Vestibulum feugiat ipsum arcu, ut rutrum sapien interdum sed.
                Etiam ullamcorper sit amet ante sed faucibus. Donec faucibus molestie diam,
                eget tincidunt urna. Suspendisse accumsan euismod urna, non faucibus justo pretium id.
            </p>
        </div>
    `
}

export const ApckCheckbox = {
    props: {
        label: {
            type: String,
            default: ""
        },
        value: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    methods: {
        check: function () {
            if (!this.disabled) {
                this.$emit('input', !this.value);
            }
        }
    },
    template: `
        <!--
        <div class="col col-sm-3 col-xs-12">
            <label class="label">
                {{ label }}
            </label>
        </div>
        <div class="col col-sm-9 col-xs-12">
            <span v-on:click="value = !value">
                <i v-if="!value" class="fa fa-square-o empty"></i>
                <i v-if="value" class="fa fa-check-square-o checked"></i>
            </span>
        </div>
        -->

        <div class="col checkbox">
            <div class="checkbox-success" v-on:click="check()">
                <input type="checkbox" v-bind:checked="value" :disabled="disabled" />
                <label>{{ label }}</label>
            </div>
        </div>
    `
}

export const ApckText = /*#__PURE__*/ Vue.extend({
    props: {
        label: {
            type: String,
            default: ""
        },
        type: {
            type: String,
            default: "text"
        },
        value: {
            type: [String, Number, Boolean],
            default: ""
        },
        rows: {
            type: [Number, String],
            default: 0
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: null
        },
        inputmode: {
            type: String,
            default: "text"
        },
        pattern: {
            type: String,
            default: null
        },
        autocomplete: {
            type: String,
            default: null
        },
        min: {
            type: [Number, String],
            default: null
        },
        max: {
            type: [Number, String],
            default: null
        },
        autofocus: {
            type: [Boolean, String],
            default: null
        },
        showLabel: {
            type: Boolean,
            default: true
        }
    },
    data: function () {
        return {
            lines: this.rows ? this.rows : 0
        };
    },
    methods: {
        focusField() {
            this.$refs.field.focus();
        },
        inputValue(val) {
            let newValue = val;
            if (this.type === 'number') {
                if (this.min) {
                    if (newValue < this.min) {
                        newValue = this.min;
                    }
                }
                if (this.max) {
                    if (newValue > this.max) {
                        newValue = this.max;
                    }
                }
            }
            this.$emit('input', newValue);
        }
    },
    template: `
        <div class="col">
            <label
                v-show="showLabel"
                class="label"
            >
                {{ label }}
            </label>
            <input
                v-if="!rows"
                :type="type"
                :disabled="disabled"
                :placeholder="placeholder"
                :inputmode="inputmode"
                :pattern="pattern"
                :autocomplete="autocomplete"
                :autofocus="autofocus"
                :min="min"
                :max="max"
                v-bind:value="value"
                v-on:input="inputValue($event.target.value)"
                v-on:keyup.enter="$emit('enter-pressed')"
                v-bind="$attrs"
                ref="field"
            />
            <div v-else class="text-wrapper">
                <textarea
                    :disabled="disabled"
                    :rows="rows"
                    :placeholder="placeholder"
                    :inputmode="inputmode"
                    v-on:input="inputValue($event.target.value)"
                    v-bind:value="value"
                    ref="field"
                ></textarea>
            </div>
        </div>
    `
});

export const ApckTOTPField = /*#__PURE__*/ ApckText.extend({
    props: {
        inputmode: {
            type: String,
            default: "numeric"
        },
        pattern: {
            type: String,
            default: "[0-9]*"
        },
        autocomplete: {
            type: String,
            default: "one-time-code"
        }
    }
});

const ApckModalGoScriptSelect = {
    data: function () {
        return {
            showScriptList: false,
            cols: [
                { data: "name", heading: "Name", canSearch: true },
                { data: "type", heading: "Type", canSearch: true, width: 90 },
            ],
            scriptName: "",
            actions: [
                {
                    title: "Select",
                    icon: "fa-crosshairs-simple",
                    class: "btn-primary",
                    handler: this.selectGoscript,
                },
            ],
        };
    },
    methods: {
        selectGoscript: function (rec) {
            const data = {}
            data.name =  rec.get("name");
            data.scriptId= rec.get("_id");
            this.$emit("select", rec);
            this.$emit("close");
        },
        newGoscript() {
            window.open(`/goscript/new`, '_blank');
        }
    },
    template: `
        <div class="new-ui">
            <apck-table
                :columns="cols"
                src="/goscript"
                :sgl-row-action="selectGoscript"
                :actions="actions"
                :new-style-table="true"
            >
                <template v-slot:tbar>
                    <ui-button
                        label="New GoScript"
                        type="primary"
                        icon-left="fa-plus"
                        @click="newGoscript"
                    />
                </template>
            </apck-table>
        </div>
    `,
};

export const ApckGoScriptImport = {
    props: {
        label: {
            type: String,
            default: ""
        },
        value: {
            type: String,
            default: null
        },
    },
    data: function () {
        return {
            showScriptList: false,
            shouldUpdateProperties: false,
            cols: [
                { "data": "name", "heading": "Name", "canSearch": true },
                { "data": "type", "heading": "Type", "canSearch": true }
            ],
            scriptName: "",
            actions: [{
                title: "Select",
                icon: "fa-plus",
                class: "btn-primary",
                handler: this.selectGoscript
            }]
        };
    },
    computed: {
        url() {
            if (this.value) return `/goscript/${this.value}/ajax`;
            return '';
        },
    },
    watch: {
        value: {
            handler(newValue) {
                this.loadName(newValue);
            },
            immediate: true,
        },
    },
    methods: {
        objToText: function(obj) {
            const protectedKeys = ["login_url", "username", "password"];
            var result = "";
            for (var key in obj) {
                if (obj.hasOwnProperty(key) && !protectedKeys.includes(key)) {
                    result += `${key}: ${obj[key]}\n`;
                }
              }
            return result;
        },
        loadName: function () {
            // output goscript name by script id
            var cmp = this;
            if (cmp.value) {
                AjaxUtils.ajax_get({
                    url: cmp.url,
                    success: function (data) {
                        // output script name
                        cmp.scriptName = data.goscript.name;
                        if (cmp.shouldUpdateProperties) {
                            // We emit the GoScript only when it is selected
                            cmp.$emit("selected", data.goscript);
                            cmp.shouldUpdateProperties = false;
                        };

                        // Disabled for now.
                        // // apply properties to scan config if we select a new GoScript.
                        // if(cmp.shouldUpdateProperties){

                        //     if ( Object.entries(data.goscript.properties).length) {
                        //         const properties_data = {
                        //             mapping: "goscripts.properties",
                        //             value: cmp.objToText(data.goscript.properties),
                        //             message: "Do you want to replace GoScript Properties",
                        //             alert: "GoScript Properties has been updated"

                        //         }
                        //         cmp.$emit("changeOption", properties_data);
                        //     };
                        //     if ( Object.entries(data.goscript.properties).length) {
                        //         const headers_data = {
                        //             mapping: "settings.http_headers",
                        //             value: cmp.objToText(data.goscript.headers),
                        //             message: "Do you want to replace Custom HTTP Headers",
                        //             alert: "Custom HTTP Headers has been updated"
                        //         }
                        //         cmp.$emit("changeOption", headers_data);
                        //     }
                        //     cmp.shouldUpdateProperties = false;
                        // }
                    },
                    fail: function () {
                        cmp.shouldUpdateProperties = false;
                        var flash = cmp.$flash({
                            type: 'error',
                            message: 'There was an error loading this GoScript'
                        });
                        cmp.scriptName = "Could not fetch GoScript.";
                    },
                    opts: {flash_form_errors: false, flash_404_errors: false}
                });
            } else {
                cmp.scriptName = "None selected";
            }

        },
        selectGoscript: function (rec) {
            const scriptId = rec.get("_id");
            this.shouldUpdateProperties = true;
            this.$emit("input", scriptId);
        },
        removeGoscript: function () {
            this.$emit("input", "");
        },
        openGoscript: function () {
            this.$lightbox.open({
                title: "Select GoScript",
                component: ApckModalGoScriptSelect,
                bind: { src: "/goscript" },
                on: {
                    select: this.selectGoscript,
                },
                size: "normal",
                // beforeclose: this.selectGoscript
            });
        },
        openGoscriptEditor() {
            window.open(`/goscript/${this.value}`, '_blank');
        },
        newGoscript() {
            window.open(`/goscript/new`, '_blank');
        }
    },
    template: `
            <div>
                <label
                    v-if="label"
                    class="label">
                    {{ label }}
                </label>
                <div class="input-horizontal">
                    <div class="input-horizontal-block">
                        <input type="text" disabled :value="scriptName" />
                    </div>
                    <div class="input-horizontal-button">
                        <apck-button
                            v-if="!value"
                            title="Select GoScript"
                            value="Select GoScript"
                            icon="fa-folder-open"
                            class="btn-primary btn-big"
                            :handler="openGoscript"
                        />
                        <apck-button
                            v-if="value"
                            title="Edit this GoScript in a new tab"
                            value="Open GoScript Editor"
                            icon="fa-external-link"
                            class="btn-secondary btn-big"
                            :handler="openGoscriptEditor"
                        />
                        <apck-button
                            v-if="value"
                            title="Remove GoScript"
                            value="Remove GoScript"
                            icon="fa-trash"
                            class="btn-negative btn-big"
                            :handler="removeGoscript"
                        />
                        <apck-button
                            v-if="!value"
                            title="Create a new GoScript in a new tab"
                            value="Create New GoScript"
                            icon="fa-plus"
                            class="btn-primary btn-big"
                            :handler="newGoscript"
                        />
                    </div>
                </div>
            </div>
    `
}

export const ApckTextUpload = {
    props: {
        label: {
            type: String,
            default: ""
        },
        value: {
            type: String,
            default: ""
        },
        rows: {
            type: [Number, String],
            default: 0
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: null
        },
        fileTerminator: {
            type: String,
            default: "\n"
        },
        fileSeparator: {
            type: String,
            default: "---\n"
        }
    },
    methods: {
        textareaUpload(evt) {
            // based on Apck.TextAreaUploadField
            var reader = new FileReader();
            var files = evt.target.files;
            var files_idx = 0;
            var cmp = this;

            var file_terminator = this.fileTerminator;
            var file_separator = this.fileSeparator;

            reader.onload = function (evt) {
                if (evt.target.readyState != 2) {
                    return;
                }
                if (evt.target.error) {
                    alert('Error while reading file');
                    return;
                }
                var old_val = cmp.value;
                var new_val = "";
                if (old_val) {
                    new_val += old_val;
                    if (old_val.slice(- file_terminator.length) !== file_terminator) {
                        new_val += file_terminator;
                    }
                    new_val += file_separator;
                }
                new_val += evt.target.result;
                if (new_val.slice(- file_terminator.length) !== file_terminator) {
                    new_val += file_terminator;
                }
                cmp.update(new_val);
                if (files_idx < files.length) {
                    reader.readAsText(files[files_idx++]);
                }
            };
            reader.readAsText(files[files_idx++]);
        },
        clickUpload: function () {
            this.$refs.file_upload.click();
        },
        update: function(value) {
            this.$emit('input', value);
        }
    },
    template: `
        <div>
            <apck-text
                :disabled="disabled"
                :label="label"
                :value="value"
                v-on:input="update"
                :rows="rows"
                :placeholder="placeholder"
            />
            <div class="col">
                <input type="file" ref="file_upload" @change="textareaUpload($event)" value="Upload" class="hidden"/>
            <apck-button
                title="Upload"
                value="Upload"
                icon="fa-upload"
                class="btn-primary"
                :handler="clickUpload"
            />
            </div>
        </div>
    `
}

export const ApckFileUpload = {
    props: {
        label: {
            type: String,
            default: "Upload File"
        },
        value: {
            type: File,
            required: false,
            default: null
        },
        rows: {
            type: Number,
            default: 0
        },
        disabled: {
            type: Boolean,
            default: false
        },
    },
    data: function () {
        return {
            file: this.value,
            uuid: createUUIDv4(),
            hovering: false,
            dragCounter: 0,
        }
    },
    methods: {
        selectFile() {
            this.file = this.$refs.file_input.files[0];
            this.$emit('input', this.file);
        },
        dragEnter(event) {
            this.dragCounter++;
        },
        dragOver(event) {
            this.hovering = true;
        },
        dragLeave(event) {
            this.dragCounter--;
            if (this.dragCounter === 0) {
                this.hovering = false;
            }
        },
        drop(event) {
            this.dragCounter = 0;
            this.hovering = false;
            this.file = event.dataTransfer.files[0];
            this.$emit('input', this.file);
        },
        clear() {
            this.file = null;
            this.$refs.file_input.value = null;
            this.$emit('input', this.file);
        },
    },
    template: `
        <div class="col">
            <label v-if="label" class="label">{{ label }}</label>

            <div :class="['input-file-upload', hovering || file ? 'active' : '']"
                 @dragenter.prevent="dragEnter($event)"
                 @dragover.prevent="dragOver($event)"
                 @dragleave.prevent="dragLeave($event)"
                 @drop.prevent="drop($event)"
            >
                <div v-if="file">
                    <label>{{ file.name }}</label>
                    <apck-button
                        title="Clear"
                        value="Clear"
                        icon="fa-trash"
                        class="btn-negative"
                        :handler="clear"
                    />
                </div>

                <div v-else>
                    <label :for="'file_' + uuid">
                       <i class="fa fa-upload"></i> Drag and drop here, or <strong>choose a file</strong> to upload.
                    </label>
                </div>

                <input :id="'file_' + uuid" type="file" @input="selectFile" ref="file_input">
            </div>
        </div>

    `
}

export const ApckPassword = {
    props: {
        label: {
            type: String,
            default: ""
        },
        value: {
            type: String,
            default: ""
        },
        showPassword: {
            type: Boolean,
            default: false
        },
    },
    data: function () {
        return {
            passwordEye: false,
        };
    },
    mounted: function () {
        // TODO: show password initial option
        // console.log(this.value);
        // if (!this.value)
        //     this.showPassword = true
    },
    methods: {
        showPasswordAction: function () {
            // TODO: eye switch
            if (this.$refs.password.type == "password") {
                this.$refs.password.type = "text";
                this.passwordEye = true;
            } else {
                this.$refs.password.type = "password"
                this.passwordEye = false;
            }
        },
        focusField() {
            this.$refs.password.focus();
        },
    },
    template: `
        <div class="col">
            <label class="label">
                {{ label }}
            </label>
            <div class="input-horizontal">
                <div class="input-horizontal-block">
                    <input type="password" ref="password" v-bind:value="value" v-on:input="$emit('input', $event.target.value)" />
                </div>
                <div v-if="showPassword" class="input-horizontal-button">
                    <apck-button
                        :title="passwordEye ? 'Hide password' : 'Show password'"
                        value=""
                        :icon="passwordEye ? 'fa-eye-slash' : 'fa-eye'"
                        class="btn-neutral btn-big"
                        :handler="showPasswordAction"
                    />
                </div>
            </div>
        </div>
    `
}

export const ApckTooltipBase = /*#__PURE__*/ Vue.extend({
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            uuid: "",
            showTooltip: false,
            tooltipTop: 0,
            tooltipBottom: 0,
            tooltipLeft: 0,
            tooltipRight: 0,
            _preventCloseOnClickOutside: false,  // set to true during an event to prevent closing the tooltip
        }
    },
    created() {
        this.uuid = createUUIDv4();
    },
    computed: {
        tooltipStyle() {
            const style = {};
            if (this.tooltipRight) {
                style['right'] = this.tooltipRight + 'px';
            } else if (this.tooltipLeft) {
                style['left'] = this.tooltipLeft + 'px';
            }

            if (this.tooltipBottom) {
                style['bottom'] = this.tooltipBottom + 'px';
            } else if (this.tooltipTop) {
                style['top'] = this.tooltipTop + 'px';
            }
            return style;
        }
    },
    methods: {
        toggleTooltip() {
            if (this.disabled) {
                return;
            }
            if (this.showTooltip) {
                this.closeTooltip();
            } else {
                this.openTooltip();
            }
        },
        openTooltip() {
            if (this.showTooltip) {
                return;
            }
            this.showTooltip = true;
            document.addEventListener('click', this.handleClickOutside);
            document.addEventListener('scroll', this.updateTooltipPosition);
            document.querySelector('.layout-main')?.addEventListener('scroll', this.updateTooltipPosition);
            eventBus.$emit('action-tooltip-opened', {uuid: this.uuid});
            eventBus.$on('action-tooltip-opened', this.handleTooltipOpened);
            this.updateTooltipPosition();
            this.onTooltipShown();
        },
        onTooltipShown() {
            // hook for subclassed components
        },
        closeTooltip() {
            if (!this.showTooltip) {
                return;
            }
            this.showTooltip = false;
            document.removeEventListener('click', this.handleClickOutside);
            document.removeEventListener('scroll', this.updateTooltipPosition);
            document.querySelector('.layout-main')?.removeEventListener('scroll', this.updateTooltipPosition);
            eventBus.$off('action-tooltip-opened', this.handleTooltipOpened);
            // reset style
            this.tooltipTop = 0;
            this.tooltipBottom = 0;
            this.tooltipLeft = 0;
            this.tooltipRight = 0;
            this.onTooltipClosed();
        },
        onTooltipClosed() {
            // hook for subclassed components
        },
        handleClickOutside(event) {
            if (this.$data._preventCloseOnClickOutside) {
                this.$data._preventCloseOnClickOutside = false;
                return;
            }
            if (this.showTooltip && !this.$el.contains(event.target)) {
                this.closeTooltip();
            }
        },
        handleTooltipOpened({uuid}) {
            if (uuid !== this.uuid) {
                this.closeTooltip();
            }
        },
        updateTooltipPosition() {
            const cmp = this;
            if (!this.showTooltip || !this.$refs.trigger) {
                return;
            }
            const parentRect = this.$refs.trigger.getBoundingClientRect();
            this.tooltipTop = parentRect.bottom;
            this.tooltipLeft = parentRect.left;

            this.$nextTick(() => {
                const tooltip = cmp.$refs.tooltip;
                if (!tooltip) return;
                const rect = tooltip.getBoundingClientRect();
                const window_height = getWindowDimensions().height;
                const window_width = getWindowDimensions().width;

                if (rect.bottom > window_height) {
                    cmp.tooltipBottom = window_height - parentRect.top;
                }
                if (rect.right > (window_width - 32)) {
                    cmp.tooltipRight = window_width - parentRect.left;
                }
            });
        },
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    },
});


export const ApckDropdownButton = /*#__PURE__*/ ApckTooltipBase.extend({
    props: {
        title: {
            type: String,
            default: ""
        },
        value: {
            type: String,
            default: ""
        },
        icon: {
            type: String,
            default: null
        },
        btnClass: {
            type: String,
            default: "btn-primary"
        },
        showCaret: {
            type: Boolean,
            default: true
        },
        closeOnSelect: {
            type: Boolean,
            default: false
        },
    },
    methods: {
        handleClickOutside(event) {
            if (this.$data._preventCloseOnClickOutside) {
                this.$data._preventCloseOnClickOutside = false;
                return;
            }
            if (this.showTooltip) {
                if ((this.closeOnSelect && this.$refs.trigger !== event.target) || !this.$el.contains(event.target)) {
                    this.closeTooltip();
                }
            }
        },
        updateTooltipPosition() {
            const cmp = this;
            if (!this.showTooltip || !this.$refs.trigger) {
                return;
            }
            const parentRect = this.$refs.trigger.getBoundingClientRect();
            this.tooltipTop = parentRect.bottom;
            this.tooltipLeft = parentRect.left;

            this.$nextTick(() => {
                const tooltip = cmp.$refs.tooltip;
                if (!tooltip) return;
                const rect = tooltip.getBoundingClientRect();
                const window_height = getWindowDimensions().height;
                const window_width = getWindowDimensions().width;

                if (rect.bottom > window_height) {
                    cmp.tooltipBottom = window_height - parentRect.top;
                }
                if (rect.right > (window_width - 32)) {
                    cmp.tooltipRight = window_width - parentRect.right;
                }
            });
        },
    },
    template: `
        <div style="position: relative;">
            <div class="apck-filter-dropdown">
                <div
                    ref="trigger"
                    :class="['dropdown btn', btnClass, disabled ? 'btn-disabled' : '', showTooltip ? 'active' : '']"
                    @click="toggleTooltip"
                    @keydown.space.prevent="toggleTooltip"
                    @keydown.esc.capture="closeTooltip"
                    tabindex="0"
                    :title="title"
                >
                    <i v-if="icon" v-bind:class="['fa', icon]" style="pointer-events: none;"/>
                    {{ value }}
                    <i v-if="showCaret"
                       class="fa fa-caret-down dropdown-arrow"
                       aria-hidden="true"
                       style="pointer-events: none;"
                    ></i>
                </div>
                <div
                    v-if="showTooltip"
                    class="dropdown-options"
                    @keydown.esc.capture="closeTooltip"
                >
                    <div class="options-list" ref="tooltip" :style="tooltipStyle">
                        <slot :closeTooltip="closeTooltip"/>
                    </div>
                </div>
            </div>
        </div>
    `
});

export const ApckFilterDateRange = {
    components: {
        'datepicker': Datepicker,
    },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        value: {
            type: String,
            default: ""
        },
        label: {
            type: String,
            default: ""
        },
        format: {
            type: String,
            default: "yyyy-MM-dd"
        },
        placeholder: {
            type: String,
            default: "Select Dates..."
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        from: {
            type: [String, Date],
            default: ''
        },
        to: {
            type: [String, Date],
            default: ''
        },
        fromDisabledDates: Object,
        toDisabledDates: Object,
    },
    computed: {
        fromValue: {
            get() {
                if (this.from instanceof Date) {
                    return this.formatDate(this.from);
                }
                return this.from;
            },
            set(value) {
                this.$emit('fromUpdated', value)
            },
        },
        toValue: {
            get() {
                if (this.to instanceof Date) {
                    return this.formatDate(this.to);
                }
                return this.to;
            },
            set(value) {
                this.$emit('toUpdated', value)
            },
        },
        displayText() {
            if (this.fromValue && this.toValue) {
                return `${this.fromValue} to ${this.toValue}`;
            } else if (this.fromValue) {
                return `${this.fromValue} to -`;
            } else if (this.toValue) {
                return `- to ${this.toValue}`;
            }
            return '';
        },
    },
    methods: {
        formatDate: function (date) {
            var cmp = this;
            try {
                var tzoffset = date.getTimezoneOffset() * 60000;
                return (new Date(date.getTime() - tzoffset)).toISOString().slice(0, 10);
            } catch (e) {
                if (date !== null) {
                    cmp.$flash({
                        type: 'error',
                        message: "Please enter a valid date in the format YYYY-MM-DD"
                    });
                }
                return '';
            }
        },
    },
    template: `
        <div class="apck-filter-dropdown">
            <label
                v-show="showLabel"
                class="label"
            >
                {{ label }}
            </label>
            <div
                class=""
            >
                <div class="btn-group">
                    <datepicker
                        @selected="event => {fromValue = formatDate(event);}"
                        :value="fromValue"
                        :format="format"
                        :disabled-dates="fromDisabledDates"
                        :disabled="disabled"
                        placeholder="Start Date"
                        typeable
                        clear-button
                        clear-button-icon="fa fa-times"
                        :calendar-button="false"
                        calendar-button-icon="fa-regular fa-calendar"
                    />
                    <span class="datepicker-to">to</span>
                    <datepicker
                        @selected="event => {toValue = formatDate(event);}"
                        :value="toValue"
                        :format="format"
                        :disabled-dates="toDisabledDates"
                        :disabled="disabled"
                        placeholder="End Date"
                        typeable
                        clear-button
                        clear-button-icon="fa fa-times"
                        :calendar-button="false"
                        calendar-button-icon="fa-regular fa-calendar"
                    />
                </div>
            </div>
        </div>
    `
}

export const ApckSelect =  /*#__PURE__*/ ApckDropdownButton.extend({
    props: {
        label: {
            type: String,
            default: ""
        },
        value: {
            type: [String, Boolean, Number, Array],
            default: ""
        },
        options: {
            type: Array,
            default: () => []
        },
        disabled: {
            type: Boolean,
            default: false
        },
        show_empty: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: false
        },
        multipleAllValue: {  // the value that represents selecting ALL options in the multiselect
            type: [String, Number],
            default: null
        },
        showLabel: {
            type: Boolean,
            default: true
        },
        vulnFilterSelect: {
            type: Boolean,
            default: false
        },
        defaultValue: {
            type: [String, Boolean, Number, Array],
            default: ''
        },
        searchable: {
            type: Boolean,
            default: false
        },
        useBrowserSelect: {  // whether to use the standard browser select element, or custom styled component
            type: Boolean,
            default: true
        },
        multipleSelectTransformer: {
            type: Function,
            default: undefined
        }
    },
    data: function () {
        return {
            searchInput: '',
            filteredOptions: {},
        }
    },
    methods: {
        onTooltipShown() {
            this.$nextTick(() => {
                this.$refs.input.focus();
            });
        },
        updateTooltipPosition() {
            if (!this.searchable && this.useBrowserSelect) {
                return;
            }
            if (!this.showTooltip || !this.$refs.trigger) {
                return;
            }
            const parentRect = this.$refs.trigger.getBoundingClientRect();
            this.tooltipTop = parentRect.bottom;
            this.tooltipLeft = parentRect.left;
        },
        select(value) {
            if (!this.multiple) {
                this.selected = value;
                this.closeTooltip();
                return;
            }
            let current = new Set(this.selected);
            if (current.has(value)) {
                current.delete(value);
            } else {
                current.add(value);
            }
            if (this.multipleAllValue) {
                // If we select the 'all' value, then deselect every other option.
                // Alternatively, if we select something other than 'all', make sure 'all' is deselected
                const all = this.multipleAllValue;
                current = value === all ? [all] : current.has(all) ? current.difference(new Set([all])) : current;
            }
            if (this.multipleSelectTransformer !== undefined) {
                current = this.multipleSelectTransformer(current, value);
            }
            this.selected = Array.from(current);
        },
        isSelected(value) {
            if (this.multiple) {
                return this.value.includes(value)
            } else {
                return this.value === value
            }
        },
        isDefaultValue(value) {
            return isEqual(value, this.defaultValue);
        }
    },
    created() {
        this.filteredOptions = this.innerOptions;
    },
    watch: {
        searchInput(value) {
            if (value) {
                this.filteredOptions = this.innerOptions.filter(
                    option => option.label.toLowerCase().includes(value.toLowerCase())
                );
            } else {
                this.filteredOptions = this.innerOptions;
            }
        },
        innerOptions(value) {
            this.filteredOptions = value;
        }
    },
    computed: {
        selected: {
            get: function() {
                return this.value;
            },
            set: function(valueSelect) {
                this.$emit("input", valueSelect);
            }
        },
        selectedLabel() {
            let selected = this.selected;
            if (this.multiple) {
                const selections = new Set(this.selected);
                if (selections.size !== 1) {
                    return `${selections.size} items selected`;
                }
                selected = this.selected[0];
            }
            const item = this.innerOptions.find(item => item.value === selected);
            return item ? item.label : '';
        },
        innerOptions() {
            const options = [];
            if (this.show_empty) {
                options.push({label: '-------', value: ''});
            }
            options.push(...this.options.map(item => {
                if (typeof item === 'string') {
                    return {
                        label: item,
                        value: item
                    };
                }
                return item;
            }));
            return options;
        }
    },
    template: `
        <div :class="[vulnFilterSelect ? 'apck-filter-select' : 'col apck-custom-select']">
            <label
                v-show="showLabel"
                class="label"
            >
                {{ label }}
            </label>

            <template v-if="!useBrowserSelect || searchable">
                <div ref="trigger"
                     :class="['apck-select-trigger', !isDefaultValue(value) ? 'active' : '']"
                     tabindex="0"
                     @click="toggleTooltip"
                     @keydown.space.prevent="toggleTooltip"
                     @keydown.esc.capture="closeTooltip"
                >
                    {{ selectedLabel }}
                    <i class="fa fa-caret-down dropdown-arrow" aria-hidden="true"></i>
                </div>
                <div
                    class="apck-select-options"
                    v-if="showTooltip"
                    ref="selectOptions"
                    :style="tooltipStyle"
                    @keydown.esc.capture="closeTooltip"
                >
                    <input
                        ref="input"
                        v-if="searchable"
                        v-model="searchInput"
                        tabindex="0"
                        placeholder="Search..."
                    />
                    <ul v-if="showTooltip">
                        <li v-for="item in filteredOptions"
                            :data-selected="isSelected(item.value)"
                            @click="select(item.value);"
                            @keydown.space.prevent="select(item.value);"
                            @keydown.enter.prevent="select(item.value);"
                            @keydown.arrow-down.prevent="$event.target.nextSibling.focus()"
                            @keydown.arrow-up.prevent="$event.target.previousSibling.focus()"
                            tabindex="0"
                        >
                            <span v-if="multiple" class="multi-checkbox"><i class="fa fa-check"></i></span>
                            {{ item.label }}
                        </li>
                    </ul>
                </div>
            </template>

            <select
                v-else
                :disabled="disabled"
                :multiple="multiple"
                v-model="selected"
                :class="[!isDefaultValue(value) ? 'active' : '']"
            >
                <option
                    v-for="item in filteredOptions"
                    :value="item.value"
                    :selected="isSelected(value)"
                >
                    {{ item.label }}
                </option>
            </select>
        </div>
    `
});


export const ApckTagItem = /*#__PURE__*/ Vue.extend({
    props: {
        id: {
            type: String,
            default: '',
        },
        name: {
            type: String,
            default: '',
        },
        color: {
            type: String,
            default: '#0099ff',
        },
    },
    methods: {
        remove() {
            this.$emit('remove', this.id);
        }
    },
    template: `
    <a
        href="javascript:;"
        :data-id="id"
        class="tag"
        :style="{ 'background': color }"
    >
        <i
            v-on:click="remove"
            class="fa fa-times"
        >
        </i>
        {{name}}
    </a>
    `
});

export const ApckTagField = {
    components: {
        'apck-tag-item': ApckTagItem,
    },
    props: {
        label: {
            type: String,
            default: ''
        },
        value: {
            type: Array,
            default() {
                return [];
            },
        },
        placeholder: {
            type: String,
            default: ''
        },
        title: {
            type: String,
            default: ''
        },
    },
    data() {
        return {
            tagInput: '',
            tags: [],
            tagNames: [],
            vert: false,
        }
    },
    computed: {
        tagfieldStyle() {
            if (this.vert) return `vert`;
            return '';
        },
    },
    mounted() {
        if (this.value) this.setIntialTags(this.value);
    },
    watch: {
        tags: {
            handler() {
                this.tagNames = this.tags.map((tag) => tag.name);
                this.$emit('update:tags', this.tags);
                this.$emit('update:tagNames', this.tagNames);
                this.$emit('input', this.tagNames); // emit input to allow binding with v-model
            }
        }
    },
    methods: {
         /**
         * Check if a tag is already included in the TagField
         * @param tag
         * @returns {boolean}
         */
        checkInc: function(tag) {
            if (this.tags.find((f) => f.name === tag.name))
                return true
            return false
        },
        setIntialTags: function(tags) {
            this.addTags(tags);
        },
        normaliseTags: function(tag) {
            var tags = [];

            if (typeof tag === 'string' && tag != '' && tag != ' ') {
                var tmp_tags = tag.split(/\r?\n/);
                for (let i = 0, l = tmp_tags.length; i < l; i++) {
                    let t = tmp_tags[i];
                    //Don't comma split URL's
                    var tmp_t = [t];//t.split(',');
                    for (var j = 0, ll = tmp_t.length; j < ll; j++) {
                        var tmp_name = tmp_t[j].trim();
                        if (tmp_name) {
                            tags.push({
                                id: createUUIDv4(),
                                name: tmp_name
                            });
                        }
                    }
                }
            } else if (tag.constructor === Array) {
                for (let i = 0, l = tag.length; i < l; i++) {
                    let t = tag[i];
                    if (typeof t === 'string') {
                        tags.push({
                            id: createUUIDv4(),
                            name: t.trim()
                        });
                    } else {
                        if(tag['name']) {
                            tag['name'] = tag['name'].trim();
                        }
                        if (t['id'] === undefined) {
                            tags.push($.extend({}, t, {id: createUUIDv4()}));
                        } else {
                            tags.push(t);
                        }
                    }
                }
            } else if (typeof tag === 'object') {
                if(tag['name']) {
                    tag['name'] = tag['name'].trim();
                }
                if (tag['id'] === undefined) {
                    tags.push($.extend({}, tag, {id: createUUIDv4()}));
                } else {
                    tags.push(tag);
                }
            }

            return tags;
        },
        checkTag: function(tag) {
            var name = tag['name'];
            if (name) {
                return {
                    id: tag['id'],
                    name: name,
                }
            }
        },
        addTags: function(tag) {
            var me = this;

            var tags = me.normaliseTags(tag);

            var newTags = [];

            for (var i = 0, l = tags.length; i < l; i++) {
                var t = tags[i];
                var found = me.checkInc(t);
                if (!found) {
                    var new_tag = me.checkTag(t);
                    if (!me.checkInc(new_tag)) {
                        // vue render
                        new_tag.color = "#828286";
                        newTags.push(new_tag);
                    }
                }
            }

            // render
            newTags.forEach(element => {
                if(!this.checkInc(element))
                    me.tags.push(element);
            });
        },
        removeTag(id) {
            const index = this.tags.findIndex((f) => f.id === id);
            if (index === -1) return;
            this.tags.splice(index, 1);
        },
        processKeyEvent(value) {
            this.tagInput = "";
            this.addTags(value);
        },
        processPasteEvent(e) {
            e.preventDefault();
            let value = "";
            if (e.clipboardData) {
                value = e.clipboardData.getData('text');
            } else {
                value = window.clipboardData.getData('text')
            }
            this.tagInput = value;
            this.addTags(value);
            this.tagInput = '';
        },
    },
    template: `
    <div class="col">
        <label class="label">
            {{ label }}
        </label>

        <div :class="['tagfield', 'infra', tagfieldStyle]">
            <div class="tagarea">
                <div class="tags">
                    <apck-tag-item
                        v-for="tag, index in tags"
                        :name="tag.name"
                        :key="tag.id"
                        :id="tag.id"
                        :color="tag.color"
                        v-on:remove="removeTag"
                    />
                </div>
                <textarea
                    class="tag-input"
                    v-model="tagInput"
                    v-on:keydown.enter.prevent="processKeyEvent($event.target.value)"
                    v-on:keydown.tab.prevent="processKeyEvent($event.target.value)"
                    v-on:blur="processKeyEvent($event.target.value)"
                    v-on:paste="processPasteEvent($event)"
                    :placeholder="placeholder"
                ></textarea>
            </div>
        </div>
    </div>
    `
}


export const ApckField = {
    props: {
        description: String,
        type: String,
        errors: {
            type: Array,
            default: null
        },
        display_error_text: {
            type: Boolean,
            default: true
        },
    },
    template: `
        <div v-bind:class="['field-holder', errors ? 'errors' : '']">
            <div class="form-field">
                <slot />
            </div>
            <div v-if="errors && display_error_text " class="error-messages">
                <p v-for="error in errors">
                    {{ error }}
                </p>
            </div>
            <p v-if="description" class="option-note option-note-new-line">{{ description }}</p>
        </div>
    `
}


export const ApckBetweenTimeFieldDay = {
    props: {
        name: String,
        number: Number,
        days: Array,
    },
    computed: {
        isChecked: function () {
            if (this.days.includes(this.number))
                return true
            return false
        }
    },
    methods: {
        check: function () {
            this.$emit('check', this.number);
        }
    },
    template: `
        <div
            class="day"
            v-on:click="check()"
            :class="{ 'check': isChecked, '': !isChecked }"
            :data-name="name"
        >
            <label>
                {{name}}
            </label>
            <i
                class="fa"
                :class="{ 'fa-check-square-o': isChecked, 'fa-square-o': !isChecked }"
            ></i>
        </div>
    `
}

export const ApckBetweenTimeField = {
    components: {
        'apck-betweentimefield-day': ApckBetweenTimeFieldDay,
    },
    // data format {"start":"22:49","end":"0:49","days":[0,6,2,4]}
    props: {
        value: {
            type: Object,
            default: () => ({}),

        },
        index: {
            type: Number,
            default: null,
        },
    },
    data: function () {
        return {
            days:[],
            start_hour: 0,
            start_min: 0,
            end_hour: 0,
            end_min: 0,
            week: [
                {
                    "name": "Sun",
                    "number": 6
                }, {
                    "name": "Mon",
                    "number": 0
                }, {
                    "name": "Tue",
                    "number": 1
                }, {
                    "name": "Wed",
                    "number": 2
                }, {
                    "name": "Thu",
                    "number": 3
                }, {
                    "name": "Fri",
                    "number": 4
                }, {
                    "name": "Sat",
                    "number": 5
                }
            ],
        };
    },
    computed: {
        valueTimeField: function () {
            return JSON.stringify({
                days: this.days,
                start: this.start_hour + ':' + this.start_min,
                end: this.end_hour + ':' + this.end_min,
            })
        }
    },
    mounted: function () {
        // init time
        let start = new Date();
        // Make end an hour ahead for default
        let end = new Date(start.valueOf() + (1000 * 60 * 60 * 2));

        if (this.value.start) {
            let start_val = this.value.start.split(':');
            start.setHours(numberRangeCheck(start_val[0], 0, 23));
            start.setMinutes(numberRangeCheck(start_val[1], 0, 59));
        }
        if (this.value.end) {
            let end_val = this.value.end.split(':');
            end.setHours(numberRangeCheck(end_val[0], 0, 23));
            end.setMinutes(numberRangeCheck(end_val[1], 0, 59));
        }

        // set component time
        this.start_hour = start.getHours();
        this.start_min = start.getMinutes();

        this.end_hour = end.getHours();
        this.end_min = end.getMinutes();

        // days
        if (!this.value.days)
            this.value.days = [];
        this.days = this.value.days;
        this.updateValue();
    },
    methods: {
        updateValue: function () {
            this.value.start = this.start_hour + ':' + this.start_min;
            this.value.end = this.end_hour + ':' + this.end_min;
            this.value.days = this.days;
        },
        updateDay: function (day) {
            let index = this.days.findIndex(f => f === day);
            if (index >= 0)
                this.days.splice(index, 1)
            else
                this.days.push(day)
            this.updateValue();
        },
        removeTimeField: function () {
            this.$emit('remove', this.index);
        },
        startHourChange: function () {
            this.start_hour = numberRangeCheck(this.start_hour, 0, 23)
            this.updateValue();
        },
        startMinChange: function () {
            this.start_min = numberRangeCheck(this.start_min, 0, 59)
            this.updateValue();
        },
        endHourChange: function () {
            this.end_hour = numberRangeCheck(this.end_hour, 0, 23)
            this.updateValue();
        },
        endMinChange: function () {
            this.end_min = numberRangeCheck(this.end_min, 0, 59)
            this.updateValue();
        }
    },
    template: `
        <div id="" class="between-time-picker">
            <div class="inputs">
                <div class="time-input start">
                    <label>Start</label>
                    <div class="time-holder">
                        <input type="text" v-model="start_hour" name="start_hour" class="hour start_hour" v-on:change="startHourChange">
                        <span>:</span>
                        <input type="text" v-model="start_min" name="start_min" class="min start_min" v-on:change="startMinChange">
                    </div>
                </div>
                <div class="time-input end">
                    <label>End</label>
                    <div class="time-holder">
                        <input type="text" v-model="end_hour" name="end_hour" class="hour end_hour" v-on:change="endHourChange">
                        <span>:</span>
                        <input type="text" v-model="end_min" name="end_min" class="min end_min" v-on:change="endMinChange">
                    </div>
                </div>
            </div>
            <div class="days">
                <label class="days-title">Days</label>
                <div class="day-holder">
                    <apck-betweentimefield-day
                        v-for="day in week"
                        :name="day.name"
                        :key="day.number"
                        :number="day.number"
                        :days="days"
                        v-on:check="updateDay"
                    />
                </div>
            </div>
            <input type="hidden" :value="valueTimeField" name="">
        <apck-button
            title="Remove"
            value="Remove"
            icon="fa-trash"
            class="btn-negative"
            :handler="removeTimeField"
        />
        </div>
    `
}

const ApckModalScheduleSelect = {
    props: {
        src: {
            type: String,
            require: true,
        },
    },
    data: function () {
        return {
            showScriptList: false,
            cols: [
                { data: "name", heading: "Name", canSearch: true },
            ],
            scriptName: "",
            actions: [
                {
                    title: "Select",
                    icon: "fa-plus",
                    class: "btn-primary",
                    handler: this.select,
                },
            ],
        };
    },
    methods: {
        select: function (rec) {
            const data = {}
            data.schedule_times= rec.get("schedule_times");
            this.$emit("select", data);
            this.$emit("close");
        },
    },
    template: `
        <apck-table title="Select Schedule Times" :columns="cols" :src="src" :row-action="select" :actions="actions" />
    `,
};

export const ApckMultiBetweenTimeField = {
    components: {
        ApckDropdown,
    },
    props: {
        // format
        // {
        //     "days": [
        //       6,
        //       1
        //     ],
        //     "start": "15:24",
        //     "end": "17:24"
        // }
        value: {
            type: Array,
            default: function () {
              return [];
            },
        },
        url_new: {
            type: String,
            default: "/schedule/new"
        },
        url_list: {
            type: String,
            default: "/schedule"
        }
    },
    data() {
        return {
            isOpenDropdown: false,
        }
    },
    computed: {
        valueWrapper() {
            return this.value.map((value_obj) => {
                return {
                    value: value_obj,
                    uuid: createUUIDv4()
                }
            });
        }
    },
    methods: {
        removeSchedule: function (index) {
            this.value.splice(index, 1);
        },
        addScheduleBtn: function() {
            this.addSchedule();
        },
        addSchedule: function (start, end, days=[]) {
            this.isOpenDropdown = false;
            this.value.push(
                {
                    start: start,
                    end: end,
                    days: days
                })
        },
        loadSchedulePattern: function (data) {
            this.$emit('input', data['schedule_times']);
        },
        saveSchedulePattern: function (value) {
            this.isOpenDropdown = false;
            const cmp = this;
            const name = value;
            // schedule_times
            const data = {
                name: name,
                schedule_times: this.value,
            };
            AjaxUtils.ajax_post({
                url: cmp.url_new,
                data: JSON.stringify(data),
                success(data) {
                    cmp.$flash({message:'Schedule Pattern has been saved successfully'});
                },
                fail: function () {
                    cmp.$dialog.alert(
                        'Failed to save the Schedule Pattern!',
                        'Schedule Pattern failed'
                    );
                }
            });
        },
        savePattern: function () {
            this.isOpenDropdown = false;
            this.$dialog.prompt("Please enter pattern name", "Save pattern", [], this.saveSchedulePattern)
        },
        loadPattern: function () {
            this.isOpenDropdown = false;
            this.$lightbox.open({
                title: "Schedule Patterns",
                component: ApckModalScheduleSelect,
                bind: { src: this.url_list },
                on: {
                    select: this.loadSchedulePattern,
                },
                size: "wide",
                // beforeclose: this.selectGoscript
            });

        }
    },
    template: `
        <ul class="options">
            <li class="option">
                    <label class="label">
                        Permitted Scanning Times
                    </label>
                    <div class="clear"></div>
                    <apck-dropdown
                        v-model="isOpenDropdown"
                    >
                        <apck-button
                            title="Add Schedule"
                            value="Add Schedule"
                            icon="fa-plus"
                            class="dropdown__item"
                            :handler="addScheduleBtn"
                        />
                        <apck-button
                            title="Add Schedule"
                            value="Load pattern"
                            icon="fa-folder-open"
                            class="dropdown__item"
                            :handler="loadPattern"
                        />
                        <apck-button
                            title="Add Schedule"
                            value="Save as pattern"
                            icon="fa-save"
                            class="dropdown__item"
                            :handler="savePattern"
                        />
                    </apck-dropdown>
                    <br/><br/>
                    <apck-betweentimefield
                        v-for="schedule_field, index in valueWrapper"
                        :value="schedule_field.value"
                        :index="index"
                        :key="schedule_field.uuid"
                        v-on:remove="removeSchedule"
                    />

                    <p class="option-note option-note-new-line"> Scheduled times can be used to set windows to control at what times the scanner will run, for example 09:00 AM to 17:30 PM Monday to Friday and 00:00 AM to 00:00 AM Saturday and Sunday Note: Changing the schedule on a already running scan will not take effect until the scan is next run. </p>
                </li>
        </ul>`

}

export const ApckDateTimePicker = {
    props: {
        label: {
            type: String,
            default: ""
        },
        value: {
            type: String,
            default: ""
        },
        disabled: {
            type: Boolean,
            default: false
        },
        placeholder: {
            type: String,
            default: null
        },
        dateOnly: {
            type: Boolean,
            default: false
        },
        timeOnly: {
            type: Boolean,
            default: false
        },
        allowClear: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        formatName: function () {
            //Format the name to snake case
            return this.label.toLowerCase().replace(/\s+/g, '_');
        },
        inputType: function () {
            if(this.dateOnly) {
                return 'date';
            }
            if (this.timeOnly) {
                return 'time';
            }

            return 'datetime-local';
        },
    },
    methods: {
        clearDate: function () {
            this.$emit('input', ''); // Clears the parent v-model value
            document.getElementById(this.formatName).value = '';  // Clears the actual input field
        }
    },
    template: `
        <div class="col c-date-picker apck-datepicker">
            <label class="label" :for="formatName">
                {{ label }}
            </label>
            <div class="input-holder">
                <input
                    autocomplete="do-not-autofill"
                    :type="inputType"
                    :id="formatName"
                    :name="formatName"
                    :value="value"
                    :disabled="disabled"
                    @input="$emit('input', $event.target.value)"
                >
                <apck-button
                    v-if="allowClear && !disabled"
                    title="Clear"
                    value="Clear"
                    class="btn-negative"
                    :handler="clearDate"
                />
            </div>
        </div>
    `
}

export const ApckColumnActions = /*#__PURE__*/ ApckTooltipBase.extend({
    props: {
        icon: {
            type: String,
            defauls: 'fa-ellipsis-h'
        }
    },
    methods: {
        updateTooltipPosition() {
            const cmp = this;
            if (!this.showTooltip || !this.$refs.trigger) {
                return;
            }
            const parentRect = this.$refs.trigger.getBoundingClientRect();
            this.tooltipTop = parentRect.top;
            this.tooltipLeft = parentRect.right;

            this.$nextTick(() => {
                const tooltip = cmp.$refs.tooltip;
                if (!tooltip) return;
                const rect = tooltip.getBoundingClientRect();
                const window_height = getWindowDimensions().height;
                const window_width = getWindowDimensions().width;

                if (rect.bottom > window_height) {
                    cmp.tooltipBottom = window_height - parentRect.top;
                }
                if (rect.right > (window_width - 32)) {
                    cmp.tooltipRight = window_width - parentRect.left;
                }
            });
        },
    },
    template: `
        <div :class="['rowActions', showTooltip ? 'tooltip-open' : '']">
            <div class="row-actions" ref="trigger" @click.stop="toggleTooltip">
                <i :class="['fa', icon]" aria-hidden="true"></i>
                <ul
                    v-show="showTooltip"
                    ref="tooltip"
                    class="actions-list"
                    :style="tooltipStyle"
                >
                    <slot :closeTooltip="closeTooltip"/>
                </ul>
            </div>
        </div>
    `
});